import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./OneriSlider.css";
import { tapHaptic } from "./utils/haptics";

/**
 * OneriSlider — Premium "Aurora Glass" öne çıkanlar şeridi
 *
 * Tip kartları: profile | place | event | news | info
 * - Buzlu cam vitrin + scroll-snap "peek" akış
 * - Tipe göre aksanlar (profile=taç, place/event/news=kapak, info=logo paneli)
 * - Gerçek veri gelmezse şerit GİZLENİR (asla demo gösterilmez)
 * - Noktalar aktif kartı yansıtır + tıklanınca o karta kayar
 * - autoplayMs > 0: nazik otomatik akış (sona varınca başa döner, dokununca duraklar)
 *
 * Prop kontratı (Feed.js buna güvenir):
 *   <OneriSlider items={[]} loading={false}
 *     onOpenProfile onOpenPlace onOpenEvent onOpenNews onOpenInfo autoplayMs={2800} />
 *   item: { id, type, title, subtitle?, avatar?, cover?, logo?, badge?, uid?, refId?, weekStamp? }
 */

/**
 * Şerit öğesi. UYDURULMADI: dosyanın başındaki prop sözleşmesi (satır 18) +
 * gövdenin okuduğu etiket/görsel yedekleri (`username`, `name`).
 */
type OneriOgesi = {
  id?: string;
  type?: string;
  title?: string;
  subtitle?: string;
  avatar?: string;
  cover?: string;
  logo?: string;
  badge?: string;
  uid?: string;
  refId?: string;
  weekStamp?: string;
  username?: string;
  name?: string;
};

// ⚠️ İNDEKS İMZASI KASITLI: arama DİNAMİK (`kinds[it.type] || kinds.info`) —
// bilinmeyen tür `info`ya düşer. Literal anahtarlı tip bu yedeği ölü kod
// gibi gösterirdi.
const kinds: Record<string, { pill: string; emoji: string }> = {
  profile: { pill: "Haftanın Profili", emoji: "⭐" },
  place:   { pill: "Haftanın Mekanı",  emoji: "📍" },
  event:   { pill: "Etkinlik",         emoji: "🎟️" },
  news:    { pill: "Güncel",           emoji: "📰" },
  info:    { pill: "Yakında",          emoji: "⚡" }
};

export default function OneriSlider({
  items,
  loading = false,
  onOpenProfile = (_it?: any) => {},
  onOpenPlace   = (_it?: any) => {},
  onOpenEvent   = (_it?: any) => {},
  onOpenNews    = (_it?: any) => {},
  onOpenInfo    = (_it?: any) => {},
  autoplayMs = 0 // 0 = kapalı; >0 nazik otomatik akış
}: {
  items?: any;
  loading?: boolean;
  onOpenProfile?: (it?: any) => void;
  onOpenPlace?: (it?: any) => void;
  onOpenEvent?: (it?: any) => void;
  onOpenNews?: (it?: any) => void;
  onOpenInfo?: (it?: any) => void;
  autoplayMs?: number;
}) {
  const data = useMemo(() => (Array.isArray(items) ? items : []), [items]);
  const hasItems = data.length > 0;
  const isLoading = loading && !hasItems;

  const scrollerRef = useRef<any>(null);
  const [active, setActive] = useState(0);
  const activeRef = useRef(0);          // interval içinde güncel index
  const rafRef = useRef(0);             // scroll throttle
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null); // autoplay
  const pauseUntilRef = useRef(0);      // bu zamana kadar autoplay duraklar

  // Bir karta hizalı kaydır (scroll-padding-left: 16 ile uyumlu)
  const scrollToCard = useCallback((i: number, behavior: ScrollBehavior = "smooth") => {
    const el = scrollerRef.current;
    if (!el) return;
    const cards = el.querySelectorAll(".os-card");
    const card = cards[i];
    if (!card) return;
    const target =
      el.scrollLeft +
      (card.getBoundingClientRect().left - el.getBoundingClientRect().left) -
      16;
    el.scrollTo({ left: Math.max(0, target), behavior });
  }, []);

  // İlk konum
  useEffect(() => {
    if (!hasItems) return;
    const t = setTimeout(() => scrollToCard(0, "auto"), 10);
    return () => clearTimeout(t);
  }, [hasItems, scrollToCard]);

  // Merkeze en yakın kartı aktif yap (rAF ile throttle)
  const onScroll = () => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      const el = scrollerRef.current;
      if (!el) return;
      const cards: any[] = Array.from(el.querySelectorAll(".os-card"));
      if (!cards.length) return;
      let best = 0;
      let bestDist = Infinity;
      const center = el.getBoundingClientRect().left + el.clientWidth / 2;
      cards.forEach((c: any, i: number) => {
        const r = c.getBoundingClientRect();
        const d = Math.abs((r.left + r.right) / 2 - center);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      if (best !== activeRef.current) {
        activeRef.current = best;
        setActive(best);
      }
    });
  };

  // Kullanıcı dokununca autoplay'i bir süre duraklat
  const pauseAutoplay = (ms = 6000) => {
    pauseUntilRef.current = Date.now() + ms;
  };

  // ── Nazik otomatik akış ───────────────────────────────────────────────
  useEffect(() => {
    if (!autoplayMs || !hasItems || data.length < 2) return;
    clearInterval(timerRef.current ?? undefined);
    timerRef.current = setInterval(() => {
      if (document.hidden) return;
      if (Date.now() < pauseUntilRef.current) return;
      const el = scrollerRef.current;
      if (!el) return;
      const count = el.querySelectorAll(".os-card").length;
      if (count < 2) return;
      const next = activeRef.current + 1 >= count ? 0 : activeRef.current + 1;
      scrollToCard(next, "smooth");
    }, autoplayMs);
    return () => clearInterval(timerRef.current ?? undefined);
  }, [autoplayMs, hasItems, data.length, scrollToCard]);

  const handleClick = (it: OneriOgesi) => {
    const t = it.type;
    if (t === "profile") return onOpenProfile(it);
    if (t === "place") return onOpenPlace(it);
    if (t === "event") return onOpenEvent(it);
    if (t === "news") return onOpenNews(it);
    return onOpenInfo(it);
  };

  // ── Yükleniyor: 3 skeleton kart ──────────────────────────────────────
  if (isLoading) {
    return (
      <section className="os-wrap os-wrap--loading" aria-label="Öne çıkanlar yükleniyor" aria-busy="true">
        <div className="os-scroller">
          {[0, 1, 2].map((i) => (
            <div key={i} className="os-card os-skel" aria-hidden="true" />
          ))}
        </div>
      </section>
    );
  }

  // ── Veri yok ve yüklenmiyor → şeridi tamamen gizle ────────────────────
  if (!hasItems) return null;

  return (
    <section className="os-wrap" aria-label="Öne çıkanlar">
      <div
        className="os-scroller"
        ref={scrollerRef}
        onScroll={onScroll}
        onPointerDown={() => pauseAutoplay()}
        onTouchStart={() => pauseAutoplay()}
        onWheel={() => pauseAutoplay()}
      >
        {data.map((it, i) => {
          const kind = kinds[it.type] || kinds.info;
          const type = kinds[it.type] ? it.type : "info";
          const label = it.title || it.username || it.name || "";
          const img = it.cover || it.avatar || "";
          return (
            <button
              key={it.id || it.uid || it.refId || i}
              type="button"
              className={`os-card os-${type} ${it.logo ? "has-logo" : ""} ${i === active ? "is-active" : ""}`}
              onClick={() => handleClick(it)}
              aria-label={`${kind.pill}: ${label}`}
            >
              {/* Tam-kapak: zengin gradient (taban) + varsa gerçek foto üstte */}
              <span className="os-bg os-bg--grad" aria-hidden="true" />
              {img ? (
                <img
                  className="os-bg os-bg-img"
                  src={img}
                  alt=""
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : null}
              <span className="os-scrim" aria-hidden="true" />

              {it.logo ? (
                <span className="os-logo" aria-hidden="true">
                  <img
                    src={it.logo}
                    alt=""
                    loading="lazy"
                    onError={(e) => { e.currentTarget.parentElement!.style.display = "none"; }}
                  />
                </span>
              ) : null}

              {type === "profile" && <span className="os-tag">★ Haftanın Profili</span>}
              {type === "news" && <span className="os-ext" aria-hidden="true">↗</span>}

              <span className="os-content">
                {type !== "profile" && <span className="os-kicker">{kind.pill}</span>}
                <span className="os-title">{label}</span>
                {it.subtitle ? <span className="os-sub">{it.subtitle}</span> : null}
              </span>
            </button>
          );
        })}
      </div>

      <div className="os-dots" role="tablist" aria-label="Kart göstergesi">
        {data.map((_, i) => (
          <button
            key={i}
            type="button"
            className={`os-dot ${i === active ? "on" : ""} pressable`}
            aria-label={`${i + 1}. karta git`}
            aria-selected={i === active}
            role="tab"
            onClick={() => { tapHaptic(); pauseAutoplay(); scrollToCard(i, "smooth"); }}
          />
        ))}
      </div>
    </section>
  );
}
